"use client";

// ---------------------------------------------------------------------------
// GC Conversion Calculator — Dropdown data & conversion logic
//
// Defines its own type list so Prednisolone and Prednisone can appear as
// separate dropdown options (the GC OPTIMIZE calculator keeps them combined).
// ---------------------------------------------------------------------------

import { HCE_MULTIPLIERS, GC_CALC_POPULATION } from './gc-calculator-data';

// ---------------------------------------------------------------------------
// Internal type definitions for conversion — separate from GC_TYPES
// ---------------------------------------------------------------------------

const GC_CONVERSION_TYPES = [
  {
    id:         'dexamethasone',
    label:      'Dexamethasone',
    multiplier: HCE_MULTIPLIERS.dexamethasone,
  },
  {
    id:         'hydrocortisone',
    label:      'Hydrocortisone',
    multiplier: HCE_MULTIPLIERS.hydrocortisone,
  },
  {
    id:                  'methylprednisolone',
    label:               'Methylprednisolone',
    multiplier:          HCE_MULTIPLIERS.methylprednisolone,
    pediatricMultiplier: HCE_MULTIPLIERS.methylprednisolonePediatric,
  },
  {
    id:                  'prednisolone',
    label:               'Prednisolone',
    multiplier:          HCE_MULTIPLIERS.prednisolonePrednisone,
    pediatricMultiplier: HCE_MULTIPLIERS.prednisolonePrednisonePediatric,
  },
  {
    id:                  'prednisone',
    label:               'Prednisone',
    multiplier:          HCE_MULTIPLIERS.prednisolonePrednisone,
    pediatricMultiplier: HCE_MULTIPLIERS.prednisolonePrednisonePediatric,
  },
];

// ---------------------------------------------------------------------------
// Dropdown item list — format for FormInputDropdown `itemList` prop
// ---------------------------------------------------------------------------

export const GC_CONVERSION_ITEMS = GC_CONVERSION_TYPES.map(({ id, label }) => ({
  label: <span className="gc-conversion-item-label">{label}</span>,
  labelText: label,
  code: id,
}));

// ---------------------------------------------------------------------------
// Patient type constants
// ---------------------------------------------------------------------------

export const GC_CONVERSION_PATIENT_TYPE = {
  PEDIATRIC: GC_CALC_POPULATION.PEDIATRIC,
  ADULT:     GC_CALC_POPULATION.ADULT,
};

// ---------------------------------------------------------------------------
// Conversion computation
// Formula: equivalentDose = (dose × fromMultiplier) / toMultiplier
// Uses pediatricMultiplier when available for pediatric patients.
//
// @param {string} fromId     — source GC type id (matches GC_TYPES[].id)
// @param {number|string} dose — source dose in mg/day
// @param {string} toId       — target GC type id
// @param {string} population — 'adult' | 'pediatric'
// @returns {number|null} Equivalent dose (rounded 2dp), or null if invalid
// ---------------------------------------------------------------------------

export function computeGcConversion(fromId, dose, toId, population) {
  const parsedDose = parseFloat(dose);
  if (!fromId || !toId || isNaN(parsedDose) || parsedDose <= 0) return null;

  const fromType = GC_CONVERSION_TYPES.find((t) => t.id === fromId);
  const toType   = GC_CONVERSION_TYPES.find((t) => t.id === toId);

  if (!fromType || !toType) return null;

  const isPediatric = population === GC_CALC_POPULATION.PEDIATRIC;

  const fromMult = (isPediatric && fromType.pediatricMultiplier) ? fromType.pediatricMultiplier : fromType.multiplier;
  const toMult   = (isPediatric && toType.pediatricMultiplier)   ? toType.pediatricMultiplier   : toType.multiplier;

  const hceTotal = parsedDose * fromMult;
  return Math.round((hceTotal / toMult) * 100) / 100;
}
